import { createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AtSign, Bell, CheckCheck, Heart, MessageSquare, Tag, UserPlus, X } from "lucide-solid";

import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  dismissNotificationMutation,
  getNotificationsQuery,
  hasUnreadNotifications,
  markAllNotificationsReadMutation,
  markNotificationReadMutation,
  notificationKind,
  queryKeys,
} from "@famidex/shared";

/**
 * 👥 Centro de notificaciones (menciones / etiquetas / follows). Local-first [L]
 * aislado por cuenta (stream `social_notifications`). Las genera el servidor (el
 * follow, o el feed para menciones/etiquetas); aquí sólo se leen, se marcan
 * leídas y se descartan. Traducción del diseño Stitch `centro_de_notificaciones`.
 */
export function NotificationsPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const userId = session.userId;

  const enabled = () => Boolean(drizzle() && userId());

  const items = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.social.notifications(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getNotificationsQuery(d, uid) : null;
    },
  });

  const anyUnread = createMemo(() => hasUnreadNotifications(items.data ?? []));

  const iconFor = (kind: string | null) => {
    switch (notificationKind(kind)) {
      case "like": {
        return Heart;
      }
      case "mention": {
        return AtSign;
      }
      case "tag": {
        return Tag;
      }
      case "message": {
        return MessageSquare;
      }
      default: {
        return UserPlus;
      }
    }
  };

  const labelFor = (kind: string | null, actor: string | null) => {
    const who = actor ?? t(msg`Alguien`);
    switch (notificationKind(kind)) {
      case "like": {
        return t(msg`A ${who} le gustó tu publicación`);
      }
      case "mention": {
        return t(msg`${who} te mencionó`);
      }
      case "tag": {
        return t(msg`${who} te etiquetó`);
      }
      case "message": {
        return t(msg`${who} te escribió`);
      }
      default: {
        return t(msg`${who} empezó a seguirte`);
      }
    }
  };

  const markRead = async (id: string) => {
    const d = drizzle();
    const uid = userId();
    if (d && uid) {
      await markNotificationReadMutation(d, uid, id);
    }
  };

  const dismiss = async (id: string) => {
    const d = drizzle();
    const uid = userId();
    if (d && uid) {
      await dismissNotificationMutation(d, uid, id);
    }
  };

  const markAll = async () => {
    const d = drizzle();
    const uid = userId();
    if (d && uid) {
      await markAllNotificationsReadMutation(d, uid);
    }
  };

  return (
    <div class="flex flex-col gap-6" data-testid="notifications-page">
      <header class="flex items-center justify-between gap-3">
        <div>
          <h1 class="text-2xl font-semibold text-foreground">{t(msg`Notificaciones`)}</h1>
          <p class="mt-1 text-sm text-muted-foreground">
            {t(msg`Menciones, etiquetas y nuevos seguidores.`)}
          </p>
        </div>
        <Show when={anyUnread()}>
          <button
            type="button"
            data-testid="notifications-mark-all"
            onClick={() => void markAll()}
            class="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-semibold text-primary"
          >
            <CheckCheck class="h-4 w-4" />
            {t(msg`Marcar todo`)}
          </button>
        </Show>
      </header>

      <Show
        when={(items.data ?? []).length > 0}
        fallback={
          <div
            data-testid="notifications-empty"
            class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-14 text-center text-muted-foreground"
          >
            <Bell class="h-8 w-8" />
            <p class="text-sm">{t(msg`Todo tranquilo en la aldea. Sin novedades.`)}</p>
          </div>
        }
      >
        <ul class="flex flex-col gap-2">
          <For each={items.data}>
            {(n) => {
              const Icon = iconFor(n.kind);
              const unread = () => n.read_at == null;
              return (
                <li
                  data-testid="notification-row"
                  data-unread={unread() ? "true" : "false"}
                  class="flex items-center gap-3 rounded-xl border border-border/60 p-3 transition-colors"
                  classList={{ "bg-accent/40": unread(), "bg-card": !unread() }}
                >
                  <span class="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <Icon class="h-5 w-5" />
                  </span>
                  <button
                    type="button"
                    onClick={() => void markRead(n.id)}
                    class="min-w-0 flex-1 text-left"
                  >
                    <p class="truncate text-sm font-medium text-foreground">
                      {labelFor(n.kind, n.actor_name)}
                    </p>
                    <Show when={n.body}>
                      <p class="truncate text-xs text-muted-foreground">{n.body}</p>
                    </Show>
                  </button>
                  <button
                    type="button"
                    aria-label={t(msg`Descartar`)}
                    data-testid="notification-dismiss"
                    onClick={() => void dismiss(n.id)}
                    class="grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted"
                  >
                    <X class="h-4 w-4" />
                  </button>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </div>
  );
}
